import React from "react";
import { useTheme } from "../contexts/ThemeContext";

import WorldClock from "./WorldClock";

interface AdminPageLayoutProps {
  /**
   * Page heading. Widened from `string` to `ReactNode` for PWA-2546, whose
   * editor puts a back control and an unsaved badge inline with the title.
   * Backward compatible -- `ReactNode` accepts `string`.
   */
  title: React.ReactNode;
  description?: React.ReactNode;
  actions?: React.ReactNode;
  children: React.ReactNode;
  headerContent?: React.ReactNode;
  hideContentScroll?: boolean;
  appearance?: "default" | "alpha";
}

const AdminPageLayout: React.FC<AdminPageLayoutProps> = ({ 
  title, 
  description, 
  actions, 
  children,
  headerContent,
  appearance = "default",
  hideContentScroll = false
}) => {
  const { theme } = useTheme();
  const isDarkMode = theme === "dark";

  if (appearance === "alpha") {
    return (
      <div className="h-full overflow-auto bg-v7-canvas font-v7-sans text-v7-ink transition-colors duration-300">
        <main className="mx-auto min-h-full w-full max-w-[1400px] px-4 py-7 md:px-10 md:py-10">
          <header className="mb-6 border-b border-v7-ink/15 pb-5">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div>
                <h1 className="text-[32px] font-light leading-9 tracking-[-0.045em] text-v7-ink md:text-4xl">{title}</h1>
                {description && <p className="mt-2 text-sm text-v7-ink-soft">{description}</p>}
              </div>
              {actions}
            </div>
            <details className="mt-3 text-xs text-v7-ink-soft">
              <summary className="w-fit cursor-pointer">World clocks</summary>
              <WorldClock />
            </details>
            {headerContent}
          </header>
          {children}
        </main>
      </div>
    );
  }

  return (
    <div className={`h-full overflow-hidden ${isDarkMode ? "bg-rp-black text-rp-grey" : "bg-gray-50 text-gray-900"}`}>
      <div className="w-full mx-auto px-4 sm:px-6 lg:px-8 py-4 h-full flex flex-col">
        {/* Header */}
        <div className="mb-4 flex-shrink-0">
          <div className="flex items-center justify-between">
            <div>
              <h1 className={`text-2xl font-bold ${isDarkMode ? "text-rp-grey" : "text-gray-900"}`}>
                {title}
              </h1>
              {description && (
                <p className={`mt-1 text-sm ${isDarkMode ? "text-rp-grey/50" : "text-gray-600"}`}>
                  {description}
                </p>
              )}
            </div>
            {actions && (
              <div className="flex space-x-3">
                {actions}
              </div>
            )}
          </div>
          
          <div className="mt-3">
             <WorldClock />
          </div>

          {headerContent && (
            <div className="mt-3">
              {headerContent}
            </div>
          )}
        </div>

        {/* Content */}
        <div className={`flex-1 min-h-0 ${hideContentScroll ? "overflow-hidden" : "overflow-auto"}`}>
          {children}
        </div>
      </div>
    </div>
  );
};

export default AdminPageLayout;
